import { useState } from 'react'
import { CalendarClock } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useRequestTargetDateChange } from '../../hooks/backend/tasks'
import { writeErrorMessage } from '../../utils/writeError'

/**
 * The door behind the gate.
 *
 * db/migrations/0121 made task.target_completion settable once: a change to a
 * date that is already set is refused by task_update_fields_v1, and the refusal
 * tells the operator to ask for the change instead. This is what it is telling
 * them to use. A gate with no door is not governance, it is an obstruction, and
 * the person who hits it at 21:00 with an owner waiting will simply stop trusting
 * the board.
 *
 * SHOWN ONLY WHEN A DATE IS ALREADY SET, because the first one needs no approval
 * and offering a request form for it would teach people that every date is a
 * negotiation. The database refuses this call on an unset date and says so.
 *
 * THE REASON IS REQUIRED HERE AS WELL AS THERE. Not belt and braces for its own
 * sake: the server's refusal arrives as a toast after a round trip, and a form
 * that lets you press the button and then tells you off is a worse form than one
 * that does not let you press it.
 */
export function TargetDateRequest({
  taskId,
  currentDate,
  approverName,
  onRequested,
}: {
  taskId: string
  currentDate: string | null
  approverName?: string | null
  onRequested?: () => void
}) {
  const request = useRequestTargetDateChange()
  const [open, setOpen] = useState(false)
  const [toDate, setToDate] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  if (!currentDate) return null

  async function submit() {
    if (!reason.trim()) {
      toast.error('Say why the date moved.')
      return
    }
    setBusy(true)
    try {
      const row = await request.trigger(
        // An empty box asks to CLEAR the date, which goes through the same
        // approval as moving it. A date quietly removed is the same broken
        // commitment as a date quietly moved, and the board has already lost one
        // that way.
        { task_id: taskId, to_date: toDate || null, reason: reason.trim() },
        { skipCache: true },
      )
      const status = (row as { status?: string } | null)?.status
      toast.success(
        status === 'Approved'
          ? 'Recorded and applied, because you hold the approval.'
          : `Sent to ${approverName ?? 'the approver'}. The date has not moved yet.`,
      )
      setOpen(false)
      setToDate('')
      setReason('')
      onRequested?.()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setOpen(true)}>
        <CalendarClock className="mr-1 h-3.5 w-3.5" />
        Request a new target date
      </Button>
    )
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border border-border p-2">
      <div className="text-xs text-muted-foreground">
        A target date is set once. This one is {currentDate}.
      </div>
      <Input
        type="date"
        value={toDate}
        onChange={(e) => setToDate(e.target.value)}
        aria-label="New target date"
      />
      <div className="text-xs text-muted-foreground">
        Leave it empty to ask for the date to be removed.
      </div>
      <Textarea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why it moved."
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={busy}>
          {busy && <Spinner className="mr-2 h-4 w-4" />}
          Ask for it
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setOpen(false)
            setToDate('')
            setReason('')
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}
